export class UploadHttpError extends Error {
  readonly status: number

  constructor(status: number) {
    super(`HTTP Error: ${status}`)
    this.name = 'UploadHttpError'
    this.status = status
  }
}

export class UploadNetworkError extends Error {
  constructor() {
    super('Network Error')
    this.name = 'UploadNetworkError'
  }
}

export interface UploadFileOptions {
  url: string
  file: File
  signal?: AbortSignal
  /**
   *
   * @param progress 0 to 100, so treat it like a percentage
   * @param file reference to the same file passed in
   * @returns void
   */
  onProgress?: (progress: number, file: File) => void
  headers?: Record<string, string>
}

export function uploadFile({
  url,
  file,
  signal,
  onProgress,
  headers = {},
}: UploadFileOptions): Promise<void> {
  // oxlint-disable-next-line promise/avoid-new
  return new Promise<void>((resolve, reject) => {
    // eslint-disable-next-line cf-web/prefer-utils-over-xhr
    const xhr = new XMLHttpRequest()

    if (signal) {
      if (signal.aborted) {
        reject(new DOMException('Upload Aborted', 'AbortError'))
        return
      }

      signal.addEventListener('abort', () => {
        xhr.abort()
      })
    }

    if (onProgress) {
      xhr.upload.onprogress = (event: ProgressEvent) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100)
          onProgress(progress, file)
        }
      }
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new UploadHttpError(xhr.status))
      }
    }

    xhr.onerror = () => {
      reject(new UploadNetworkError())
    }

    xhr.onabort = () => {
      reject(new DOMException('Upload Aborted', 'AbortError'))
    }

    xhr.open('PUT', url)

    xhr.setRequestHeader('Content-Type', file.type)

    Object.entries(headers).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value)
    })

    xhr.send(file)
  })
}
